import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaAws,
  FaGoogle,
  FaDocker,
  FaPython,
  FaJava,
  FaReact,
  FaNodeJs,
  FaGitAlt,
} from "react-icons/fa";
import {
  SiTerraform,
  SiAnsible,
  SiOracle,
  SiMongodb,
  SiPostgresql,
  SiKubernetes,
  SiSnowflake,
} from "react-icons/si";

// Skill data categorized
const skillsData = {
  "Cloud Security": [
    { name: "AWS", icon: <FaAws />, proficiency: 90, color: "#FF9900" },
    {
      name: "Google Cloud",
      icon: <FaGoogle />,
      proficiency: 85,
      color: "#4285F4",
    },
    {
      name: "Kubernetes",
      icon: <SiKubernetes />,
      proficiency: 85,
      color: "#326CE5",
    },
    {
      name: "Terraform",
      icon: <SiTerraform />,
      proficiency: 80,
      color: "#7B42BC",
    },
  ],
  "DevOps Tools": [
    { name: "Docker", icon: <FaDocker />, proficiency: 90, color: "#2496ED" },
    { name: "Ansible", icon: <SiAnsible />, proficiency: 75, color: "#EE0000" },
  ],
  "Programming Languages": [
    { name: "Python", icon: <FaPython />, proficiency: 95, color: "#3776AB" },
    { name: "Java", icon: <FaJava />, proficiency: 85, color: "#007396" },
    { name: "React", icon: <FaReact />, proficiency: 80, color: "#61DAFB" },
    { name: "NodeJS", icon: <FaNodeJs />, proficiency: 80, color: "#339933" },
  ],
  Databases: [
    {
      name: "Oracle SQL",
      icon: <SiOracle />,
      proficiency: 70,
      color: "#F80000",
    },
    { name: "MongoDB", icon: <SiMongodb />, proficiency: 75, color: "#47A248" },
    {
      name: "PostgreSQL",
      icon: <SiPostgresql />,
      proficiency: 70,
      color: "#4169E1",
    },
    {
      name: "Snowflake",
      icon: <SiSnowflake />,
      proficiency: 75,
      color: "#29B5E8",
    },
  ],
};

const Skills = () => {
  const [selectedCategory, setSelectedCategory] = useState("Cloud Security");

  // Function to handle smooth scroll
  const handleScrollToSection = (category) => {
    setSelectedCategory(category);
    const section = document.getElementById(category);
    section.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="py-12">
      <div className="container mx-auto px-4">
        {/* Animated Section Header */}
        <motion.h2
          className="text-4xl sm:text-5xl font-bold text-center mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          Technical Expertise
        </motion.h2>

        {/* Skill Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-12"
        >
          {Object.keys(skillsData).map((category) => (
            <button
              key={category}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ${
                selectedCategory === category
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25"
                  : "bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white border border-white/10"
              }`}
              onClick={() => handleScrollToSection(category)}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Display Skills by Category */}
        {Object.keys(skillsData).map((category, categoryIndex) => (
          <motion.div
            id={category}
            key={category}
            className="mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl sm:text-3xl font-semibold text-center mb-8 text-white">
              {category}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {skillsData[category].map((skill, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 group"
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div
                    className="flex items-center justify-center mb-4 text-5xl sm:text-6xl transition-all duration-300 group-hover:scale-110"
                    style={{ color: skill.color }}
                  >
                    {skill.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-center mb-4 text-white">
                    {skill.name}
                  </h3>

                  {/* Proficiency Bar */}
                  <div className="relative w-full bg-white/10 rounded-full h-3 mb-2 overflow-hidden">
                    <motion.div
                      className="absolute top-0 left-0 h-full rounded-full"
                      style={{ backgroundColor: skill.color }}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.proficiency}%` }}
                      transition={{ duration: 1.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <p className="text-center text-gray-400 text-sm">
                    {skill.proficiency}% Proficiency
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Skills;
